import { useState, useEffect } from 'react';
import { accountsAPI, transfersAPI, fxAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeftRight, Globe, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import TransferConfirmation from '../components/TransferConfirmation';

const CURRENCIES = ['EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR', 'MXN', 'BRL', 'SGD', 'HKD'];

export default function TransferPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('domestic');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);

  // Domestic
  const [dFromAccount, setDFromAccount] = useState('');
  const [dToName, setDToName] = useState('');
  const [dToNumber, setDToNumber] = useState('');
  const [dAmount, setDAmount] = useState('');
  const [dMemo, setDMemo] = useState('');

  // International
  const [iFromAccount, setIFromAccount] = useState('');
  const [iRecipientName, setIRecipientName] = useState('');
  const [iCountry, setICountry] = useState('');
  const [iSwift, setISwift] = useState('');
  const [iAmount, setIAmount] = useState('');
  const [iCurrency, setICurrency] = useState('EUR');
  const [iMemo, setIMemo] = useState('');
  const [fxQuote, setFxQuote] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountsAPI.list();
      setAccounts(res.data.accounts);
      if (res.data.accounts.length > 0) {
        setDFromAccount(res.data.accounts[0]._id);
        setIFromAccount(res.data.accounts[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load accounts');
    }
  };

  const handleDomesticTransfer = async (e) => {
    e.preventDefault();
    if (!dAmount || parseFloat(dAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      await transfersAPI.domestic({
        from_account_id: dFromAccount,
        to_account_name: dToName,
        to_account_number: dToNumber,
        amount: parseFloat(dAmount),
        memo: dMemo
      });
      setConfirmDetails({
        type: 'Domestic Transfer',
        amount: parseFloat(dAmount),
        recipient: dToName || dToNumber,
        fromAccount: selectedDAccount?.name || 'Account',
      });
      setConfirmOpen(true);
      setDToName(''); setDToNumber(''); setDAmount(''); setDMemo('');
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const getQuote = async () => {
    if (!iAmount || parseFloat(iAmount) <= 0) { toast.error('Enter amount first'); return; }
    try {
      const res = await fxAPI.quote(parseFloat(iAmount), iCurrency);
      setFxQuote(res.data);
    } catch (err) {
      toast.error('Failed to get quote');
    }
  };

  const handleInternationalTransfer = async (e) => {
    e.preventDefault();
    if (!iAmount || parseFloat(iAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const res = await transfersAPI.international({
        from_account_id: iFromAccount,
        recipient_name: iRecipientName,
        recipient_country: iCountry,
        swift_code: iSwift,
        amount: parseFloat(iAmount),
        currency: iCurrency,
        memo: iMemo
      });
      setConfirmDetails({
        type: 'International Transfer',
        amount: parseFloat(iAmount),
        recipient: `${iRecipientName} (${iCountry})`,
        fromAccount: selectedIAccount?.name || 'Account',
        convertedAmount: res.data.converted_amount,
        fxRate: res.data.fx_rate,
        targetCurrency: iCurrency,
      });
      setConfirmOpen(true);
      setIRecipientName(''); setICountry(''); setISwift(''); setIAmount(''); setIMemo('');
      setFxQuote(null);
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedDAccount = accounts.find(a => a._id === dFromAccount);
  const selectedIAccount = accounts.find(a => a._id === iFromAccount);

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Transfer Money</h2>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="transfer-tabs">
        <TabsList className="mb-6">
          <TabsTrigger value="domestic" data-testid="transfer-domestic-tab">
            <ArrowLeftRight className="h-4 w-4 mr-2" /> Domestic
          </TabsTrigger>
          <TabsTrigger value="international" data-testid="transfer-international-tab">
            <Globe className="h-4 w-4 mr-2" /> International
          </TabsTrigger>
        </TabsList>

        <TabsContent value="domestic">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Domestic Transfer</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleDomesticTransfer} className="space-y-4">
                <div className="space-y-2">
                  <Label>From Account</Label>
                  <Select value={dFromAccount} onValueChange={setDFromAccount} data-testid="transfer-from-account-select">
                    <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => (
                        <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedDAccount && <p className="text-xs text-muted-foreground">Available: {formatCurrency(selectedDAccount.available_balance)}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Recipient Name</Label>
                  <Input data-testid="transfer-to-recipient-input" placeholder="John Smith" value={dToName} onChange={e => setDToName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Account Number (Optional)</Label>
                  <Input placeholder="Account number" value={dToNumber} onChange={e => setDToNumber(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Amount (USD)</Label>
                  <Input data-testid="transfer-amount-input" type="number" step="0.01" min="0.01" placeholder="0.00" value={dAmount} onChange={e => setDAmount(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Memo (Optional)</Label>
                  <Textarea placeholder="Add a note" value={dMemo} onChange={e => setDMemo(e.target.value)} />
                </div>
                <Button data-testid="transfer-submit-button" type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Processing...' : 'Send Transfer'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="international">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>International Transfer</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInternationalTransfer} className="space-y-4">
                  <div className="space-y-2">
                    <Label>From Account</Label>
                    <Select value={iFromAccount} onValueChange={setIFromAccount}>
                      <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent>
                        {accounts.map(a => (
                          <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedIAccount && <p className="text-xs text-muted-foreground">Available: {formatCurrency(selectedIAccount.available_balance)}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Recipient Name</Label>
                      <Input placeholder="Full name" value={iRecipientName} onChange={e => setIRecipientName(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Input placeholder="United Kingdom" value={iCountry} onChange={e => setICountry(e.target.value)} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>SWIFT/BIC Code</Label>
                    <Input placeholder="BARCGB22" value={iSwift} onChange={e => setISwift(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount (USD)</Label>
                      <Input type="number" step="0.01" min="0.01" placeholder="0.00" value={iAmount} onChange={e => { setIAmount(e.target.value); setFxQuote(null); }} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Currency</Label>
                      <Select value={iCurrency} onValueChange={v => { setICurrency(v); setFxQuote(null); }} data-testid="transfer-currency-select">
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="button" variant="outline" onClick={getQuote} data-testid="transfer-get-quote-button">
                    Get FX Quote
                  </Button>
                  <div className="space-y-2">
                    <Label>Memo</Label>
                    <Textarea placeholder="Purpose of transfer" value={iMemo} onChange={e => setIMemo(e.target.value)} />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="transfer-submit-button">
                    {loading ? 'Processing...' : 'Send International Transfer'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* FX Quote Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>FX Quote</CardTitle>
              </CardHeader>
              <CardContent>
                {fxQuote ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground">You Send</p>
                      <p className="text-xl font-semibold tabular-nums">{formatCurrency(fxQuote.amount_usd)}</p>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <ArrowUpRight className="h-4 w-4" />
                      <span className="text-sm">Rate: 1 USD = {fxQuote.rate} {fxQuote.currency}</span>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Recipient Gets</p>
                      <p className="text-xl font-semibold tabular-nums text-green-600">
                        {fxQuote.symbol}{fxQuote.converted_amount.toLocaleString()}
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Fee: $0.00 (waived)</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Enter amount and click "Get FX Quote" to preview conversion.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <TransferConfirmation open={confirmOpen} onClose={setConfirmOpen} transferDetails={confirmDetails} />
    </div>
  );
}
